import React, { useState } from "react";
import axios from "axios";
import { X, Phone, ShieldCheck, CheckCircle2, Lock, Clock } from "lucide-react";
import { SHOP } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { useBackClose } from "@/lib/backstack";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  return String(detail);
}

const inputCls = "w-full h-11 px-3 rounded-lg border border-gray-300 focus:border-[#0056D2] focus:ring-2 focus:ring-[#0056D2]/20 outline-none text-sm";
const btnCls = "mt-4 w-full h-12 rounded-lg bg-[#FF5722] hover:bg-[#E64A19] disabled:opacity-60 text-white font-extrabold uppercase tracking-wide text-sm transition-colors";

export default function LoginDialog({ open, onClose }) {
  const { loginSuccess } = useAuth();
  useBackClose(open, () => onClose?.());
  const [stage, setStage] = useState("login"); // login | register | pending | otp | done | forgot | resetDone
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [business, setBusiness] = useState("");
  const [otp, setOtp] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const reset = () => {
    setStage("login"); setPhone(""); setPassword(""); setName(""); setBusiness(""); setOtp(""); setCode(""); setNewPassword(""); setErr("");
  };

  const handleClose = () => { onClose?.(); setTimeout(reset, 200); };

  const doLogin = async () => {
    setErr("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { phone, password });
      if (res.data.needs_otp) { setStage("otp"); return; }
      loginSuccess(res.data.token, res.data.user);
      setStage("done");
    } catch (e) {
      const d = formatDetail(e?.response?.data?.detail);
      if (d === "PENDING_APPROVAL") setStage("pending");
      else if (d === "BLOCKED") setErr("Your account has been blocked. Please contact Adeshwar Electricals on 080-41249285.");
      else setErr(d || "Login failed.");
    } finally { setLoading(false); }
  };

  const doRegister = async () => {
    setErr("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, { phone, password, name, business_name: business });
      setStage("pending");
    } catch (e) {
      setErr(formatDetail(e?.response?.data?.detail) || "Registration failed.");
    } finally { setLoading(false); }
  };

  const doVerifyOtp = async () => {
    setErr("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-otp`, { phone, otp });
      loginSuccess(res.data.token, res.data.user);
      setStage("done");
    } catch (e) {
      setErr(formatDetail(e?.response?.data?.detail) || "Invalid OTP.");
    } finally { setLoading(false); }
  };

  const doForgot = async () => {
    setErr("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { phone, code, new_password: newPassword });
      setPassword("");
      setStage("resetDone");
    } catch (e) {
      setErr(formatDetail(e?.response?.data?.detail) || "Could not reset password.");
    } finally { setLoading(false); }
  };

  if (!open) return null;

  return (
    <>
      <div onClick={handleClose} className="fixed inset-0 bg-black/60 z-50" />
      <div
        data-testid={SHOP.loginDialog}
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[92vw] max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
      >
        <div className="relative bg-gradient-to-br from-[#0056D2] to-[#0043A6] px-6 py-6 text-white">
          <button onClick={handleClose} className="absolute top-3 right-3 w-9 h-9 rounded-full hover:bg-white/10 grid place-items-center" aria-label="Close">
            <X size={18} />
          </button>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFD700] text-gray-900 text-[10px] font-extrabold uppercase tracking-wider">
            <ShieldCheck size={12} strokeWidth={3} /> Trade Partner Login
          </div>
          <h3 className="font-display text-2xl font-black mt-3">
            {stage === "register" ? "New account" : stage === "otp" ? "Verify OTP" : stage === "pending" ? "Awaiting approval" : stage === "forgot" ? "Forgot password" : stage === "resetDone" ? "Password reset" : "Welcome back"}
          </h3>
          <p className="text-sm text-blue-100 mt-1">
            {stage === "login" && "Login to view trade prices & place orders"}
            {stage === "register" && "Register your business — we approve every partner"}
            {stage === "otp" && "Enter the OTP shared by Adeshwar Electricals"}
            {stage === "pending" && "Your account is being reviewed"}
            {stage === "forgot" && "Verify with the first 6 digits of your mobile & set a new password"}
            {stage === "resetDone" && "You can now login with your new password"}
            {stage === "done" && "You're all set!"}
          </p>
        </div>

        <div className="p-6">
          {stage === "login" && (
            <>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">Mobile number</label>
              <div className="relative mb-3">
                <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input data-testid={SHOP.loginPhoneInput} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="9876543210" className={`${inputCls} pl-10`} />
              </div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={`${inputCls} pl-10`} onKeyDown={(e) => e.key === "Enter" && doLogin()} />
              </div>
              {err && <p className="text-sm text-[#EF4444] mt-2 font-semibold">{err}</p>}
              <button data-testid="login-submit-btn" disabled={loading} onClick={doLogin} className={btnCls}>
                {loading ? "Logging in..." : "Login"}
              </button>
              <button data-testid="forgot-password-btn" onClick={() => { setErr(""); setStage("forgot"); }} className="mt-2 w-full h-9 text-sm text-gray-600 font-semibold hover:text-[#0056D2] hover:underline">
                Forgot password?
              </button>
              <button data-testid="show-register-btn" onClick={() => { setErr(""); setStage("register"); }} className="mt-1 w-full h-10 text-sm text-[#0056D2] font-bold hover:underline">
                New to Adeshwar? Register your business →
              </button>
              <div className="flex items-center gap-3 my-3">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">or</span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>
              <button
                data-testid="google-signin-btn"
                onClick={() => {
                  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                  const redirectUrl = window.location.origin + "/";
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full h-11 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-800 font-bold text-sm flex items-center justify-center gap-2.5"
              >
                <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.1 18.9 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C41 35.4 44 30.2 44 24c0-1.3-.1-2.6-.4-3.9z"/></svg>
                Continue with Google
              </button>
              <p className="text-[10px] text-gray-400 text-center mt-2">Google accounts also need Adeshwar approval before trade prices unlock</p>
            </>
          )}

          {stage === "forgot" && (
            <>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">Registered mobile number</label>
              <div className="relative">
                <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input data-testid="forgot-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="9876543210" className={`${inputCls} pl-10`} />
              </div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5 mt-3">Verification code — first 6 digits of your mobile</label>
              <input
                data-testid="forgot-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="987654"
                maxLength={6}
                className="w-full h-12 px-3 rounded-lg border border-gray-300 focus:border-[#0056D2] focus:ring-2 focus:ring-[#0056D2]/20 outline-none text-lg tracking-[0.5em] font-bold text-center"
              />
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5 mt-3">New password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input data-testid="forgot-new-password-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 4 characters" className={`${inputCls} pl-10`} onKeyDown={(e) => e.key === "Enter" && doForgot()} />
              </div>
              <p className="text-xs text-gray-500 mt-2">Available for approved trade partners only. Enter the <span className="font-bold text-gray-700">first 6 digits of your registered mobile number</span> as the verification code.</p>
              {err && <p className="text-sm text-[#EF4444] mt-2 font-semibold">{err}</p>}
              <button data-testid="forgot-submit-btn" disabled={loading || !phone.trim() || code.length !== 6 || newPassword.length < 4} onClick={doForgot} className={btnCls}>
                {loading ? "Resetting..." : "Reset Password"}
              </button>
              <button onClick={() => { setErr(""); setStage("login"); }} className="mt-3 w-full h-10 text-sm text-gray-600 font-semibold hover:text-gray-900">
                ← Back to login
              </button>
            </>
          )}

          {stage === "resetDone" && (
            <div data-testid="reset-done" className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-[#16A34A]/10 grid place-items-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-[#16A34A]" />
              </div>
              <h4 className="font-display text-xl font-black text-gray-900">Password reset!</h4>
              <p className="text-sm text-gray-600 mt-3">Login with your mobile number and your <span className="font-bold">new password</span>.</p>
              <button data-testid="reset-done-login-btn" onClick={() => { setErr(""); setStage("login"); }} className="mt-6 w-full h-11 rounded-lg bg-gray-900 hover:bg-gray-800 text-white font-bold text-sm">
                Login Now
              </button>
            </div>
          )}

          {stage === "register" && (
            <>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">Your name</label>
              <input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Rajesh Kumar" className={`${inputCls} mb-3`} />
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">Shop / business name</label>
              <input data-testid="register-business-input" value={business} onChange={(e) => setBusiness(e.target.value)} placeholder="Kumar Electricals" className={`${inputCls} mb-3`} />
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">Mobile number</label>
              <input data-testid="register-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="9876543210" className={`${inputCls} mb-3`} />
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">Password</label>
              <input data-testid="register-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 4 characters" className={inputCls} />
              {err && <p className="text-sm text-[#EF4444] mt-2 font-semibold">{err}</p>}
              <button data-testid="register-submit-btn" disabled={loading} onClick={doRegister} className={btnCls}>
                {loading ? "Registering..." : "Register"}
              </button>
              <button onClick={() => { setErr(""); setStage("login"); }} className="mt-3 w-full h-10 text-sm text-gray-600 font-semibold hover:text-gray-900">
                ← Back to login
              </button>
            </>
          )}

          {stage === "pending" && (
            <div data-testid="pending-approval" className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-[#FFD700]/20 grid place-items-center mx-auto mb-4">
                <Clock size={30} className="text-[#B8860B]" />
              </div>
              <h4 className="font-display text-xl font-black text-gray-900">Approval pending</h4>
              <p className="text-sm text-gray-600 mt-2">
                Adeshwar Electricals will verify your business and send you an <b>OTP on WhatsApp</b>. Once you receive it, login again and enter the OTP.
              </p>
              <p className="text-xs text-gray-500 mt-3">Questions? Call 080-41249285</p>
              <button onClick={() => { setErr(""); setStage("login"); }} className="mt-6 w-full h-11 rounded-lg bg-gray-900 hover:bg-gray-800 text-white font-bold text-sm">
                Back to Login
              </button>
            </div>
          )}

          {stage === "otp" && (
            <>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">6-digit OTP</label>
              <input
                data-testid={SHOP.loginOtpInput}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                maxLength={6}
                className="w-full h-12 px-3 rounded-lg border border-gray-300 focus:border-[#0056D2] focus:ring-2 focus:ring-[#0056D2]/20 outline-none text-lg tracking-[0.5em] font-bold text-center"
              />
              <p className="text-xs text-gray-500 mt-2">The OTP was shared with you on WhatsApp by Adeshwar Electricals after approval.</p>
              {err && <p className="text-sm text-[#EF4444] mt-2 font-semibold">{err}</p>}
              <button data-testid={SHOP.loginVerifyBtn} disabled={loading} onClick={doVerifyOtp} className={btnCls}>
                {loading ? "Verifying..." : "Verify & Login"}
              </button>
              <button onClick={() => { setErr(""); setStage("login"); }} className="mt-3 w-full h-10 text-sm text-gray-600 font-semibold hover:text-gray-900">
                ← Back
              </button>
            </>
          )}

          {stage === "done" && (
            <div data-testid={SHOP.loginSuccess} className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-[#16A34A]/10 grid place-items-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-[#16A34A]" />
              </div>
              <h4 className="font-display text-xl font-black text-gray-900">You&apos;re logged in!</h4>
              <p className="text-sm text-gray-600 mt-1">Trade prices are now visible. Happy ordering!</p>
              <button onClick={handleClose} className="mt-6 w-full h-11 rounded-lg bg-gray-900 hover:bg-gray-800 text-white font-bold text-sm">
                Start Shopping
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
